// Typed client for the same-origin /api proxy to showcase-control. The local
// showcase token is injected by the dev server / container proxy, never here.

export interface Brand {
  name: string;
  tagline: string;
  logo_text: string;
}

export interface GameInfo {
  id: string;
  name: string;
  volatility: string;
  declared_rtp: string;
  rtp_disclaimer: string;
  mobile_support: boolean;
}

/** A single launchable showcase game card (from /api/config games[]). */
export interface GameDef {
  id: string;
  name: string;
  description: string;
  volatility: string;
  declared_rtp: string;
  rtp_disclaimer: string;
  mobile_support: boolean;
  enabled: boolean;
}

export interface ShowcaseConfig {
  mode: string;
  demo_indicator: string;
  brand: Brand;
  lan_mode: boolean;
  token_required: boolean;
  currencies: string[];
  languages: string[];
  game: GameInfo;
  /** All ENABLED games the lobby may launch. Rendered one card per game. */
  games: GameDef[];
  urls: Record<string, string>;
  disclaimer: string[];
}

export interface DemoPlayer {
  slot: string;
  display_name: string;
  language: string;
  currency: string;
  starting_minor: number;
  external_player_id: string;
  preset: boolean;
}

export interface Balance {
  slot: string;
  mode: string;
  balance_minor: number;
  currency: string;
  balance_version: number;
}

export interface LaunchResult {
  slot: string;
  game_id: string;
  mode: string;
  launch_url: string;
  game_gateway_url: string;
  origin: string;
  session_id: string;
  expires_at_unix: number;
}

export interface RoundSummary {
  round_id: string;
  state: string;
  total_bet_minor: number;
  total_win_minor: number;
  currency: string;
  observed_at_ms: number;
}

export interface ServiceHealth {
  name: string;
  status: string;
  ready: boolean;
  latency_ms?: number;
  error?: string;
}

export interface CreatePlayerInput {
  display_name: string;
  starting_balance_minor: number;
  language: string;
  currency: string;
}

// --- defensive normalization -------------------------------------------------
// showcase-control is the source of truth, but legacy/edge responses (or a
// transient proxy error) can return null, omit a field, or send a malformed
// row. The UI treats collections as arrays unconditionally, so we normalize
// every list to [] and drop malformed entries before they reach React.

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export function normalizeRound(v: unknown): RoundSummary | null {
  if (!isObject(v)) return null;
  if (typeof v.round_id !== 'string' || v.round_id === '') return null;
  return {
    round_id: v.round_id,
    state: str(v.state, 'UNKNOWN'),
    total_bet_minor: num(v.total_bet_minor),
    total_win_minor: num(v.total_win_minor),
    currency: str(v.currency),
    observed_at_ms: num(v.observed_at_ms),
  };
}

export function normalizeRounds(v: unknown): RoundSummary[] {
  return asArray(v)
    .map(normalizeRound)
    .filter((r): r is RoundSummary => r !== null);
}

export function normalizePlayer(v: unknown): DemoPlayer | null {
  if (!isObject(v)) return null;
  if (typeof v.slot !== 'string' || v.slot === '') return null;
  return {
    slot: v.slot,
    display_name: str(v.display_name, v.slot),
    language: str(v.language, 'en'),
    currency: str(v.currency, 'USD'),
    starting_minor: num(v.starting_minor),
    external_player_id: str(v.external_player_id),
    preset: v.preset === true,
  };
}

export function normalizePlayers(v: unknown): DemoPlayer[] {
  return asArray(v)
    .map(normalizePlayer)
    .filter((p): p is DemoPlayer => p !== null);
}

export function normalizeGame(v: unknown): GameDef | null {
  if (!isObject(v)) return null;
  if (typeof v.id !== 'string' || v.id === '') return null;
  return {
    id: v.id,
    name: str(v.name, v.id),
    description: str(v.description),
    volatility: str(v.volatility, 'high'),
    declared_rtp: str(v.declared_rtp),
    rtp_disclaimer: str(v.rtp_disclaimer),
    mobile_support: v.mobile_support !== false,
    enabled: v.enabled !== false,
  };
}

export function normalizeGames(v: unknown): GameDef[] {
  return asArray(v)
    .map(normalizeGame)
    .filter((g): g is GameDef => g !== null && g.enabled);
}

export function normalizeService(v: unknown): ServiceHealth | null {
  if (!isObject(v)) return null;
  if (typeof v.name !== 'string' || v.name === '') return null;
  const out: ServiceHealth = {
    name: v.name,
    status: str(v.status, 'unknown'),
    ready: v.ready === true,
  };
  if (typeof v.latency_ms === 'number' && Number.isFinite(v.latency_ms)) out.latency_ms = v.latency_ms;
  if (typeof v.error === 'string') out.error = v.error;
  return out;
}

export function normalizeServices(v: unknown): ServiceHealth[] {
  return asArray(v)
    .map(normalizeService)
    .filter((s): s is ServiceHealth => s !== null);
}

export function normalizeConfig(v: unknown): ShowcaseConfig {
  const o = isObject(v) ? v : {};
  const brand = isObject(o.brand) ? o.brand : {};
  const game = isObject(o.game) ? o.game : {};
  const urls = isObject(o.urls) ? (o.urls as Record<string, unknown>) : {};
  // games[] is preferred; fall back to a single-element list derived from the
  // legacy `game` object so older control planes still render one card.
  let games = normalizeGames(o.games);
  if (games.length === 0 && typeof game.id === 'string' && game.id !== '') {
    const single = normalizeGame({ ...game, enabled: true });
    if (single) games = [single];
  }
  const stringRecord: Record<string, string> = {};
  for (const [k, val] of Object.entries(urls)) {
    if (typeof val === 'string') stringRecord[k] = val;
  }
  return {
    mode: str(o.mode, 'demo'),
    demo_indicator: str(o.demo_indicator, 'VIRTUAL CREDITS'),
    brand: {
      name: str(brand.name, 'AeroPlay Studios'),
      tagline: str(brand.tagline),
      logo_text: str(brand.logo_text, 'AeroPlay'),
    },
    lan_mode: o.lan_mode === true,
    token_required: o.token_required !== false,
    currencies: asArray(o.currencies).filter((c): c is string => typeof c === 'string'),
    languages: asArray(o.languages).filter((l): l is string => typeof l === 'string'),
    game: {
      id: str(game.id),
      name: str(game.name, 'Aero Fruits'),
      volatility: str(game.volatility, 'high'),
      declared_rtp: str(game.declared_rtp),
      rtp_disclaimer: str(game.rtp_disclaimer),
      mobile_support: game.mobile_support !== false,
    },
    games,
    urls: stringRecord,
    disclaimer: asArray(o.disclaimer).filter((d): d is string => typeof d === 'string'),
  };
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) detail = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export const api = {
  getConfig: async (): Promise<ShowcaseConfig> => normalizeConfig(await req<unknown>('/api/config')),
  listPlayers: async (): Promise<{ players: DemoPlayer[] }> => {
    const r = await req<{ players?: unknown }>('/api/players');
    return { players: normalizePlayers(r.players) };
  },
  createPlayer: async (input: CreatePlayerInput): Promise<{ player: DemoPlayer }> => {
    const r = await req<{ player?: unknown }>('/api/players', { method: 'POST', body: JSON.stringify(input) });
    const player = normalizePlayer(r.player);
    if (!player) throw new Error('invalid player payload');
    return { player };
  },
  balance: (slot: string): Promise<Balance> => req<Balance>(`/api/players/${encodeURIComponent(slot)}/balance`),
  reset: async (slot: string): Promise<{ player: DemoPlayer }> => {
    const r = await req<{ player?: unknown }>(`/api/players/${encodeURIComponent(slot)}/reset`, { method: 'POST' });
    const player = normalizePlayer(r.player);
    if (!player) throw new Error('invalid player payload');
    return { player };
  },
  launch: (slot: string, gameId?: string): Promise<LaunchResult> =>
    req<LaunchResult>('/api/launch', {
      method: 'POST',
      body: JSON.stringify(gameId ? { slot, game_id: gameId } : { slot }),
    }),
  observeRound: (slot: string, roundId: string): Promise<unknown> =>
    req<unknown>('/api/rounds/observed', { method: 'POST', body: JSON.stringify({ slot, round_id: roundId }) }),
  recentRounds: async (slot: string): Promise<{ rounds: RoundSummary[] }> => {
    const r = await req<{ rounds?: unknown }>(`/api/rounds?slot=${encodeURIComponent(slot)}`);
    return { rounds: normalizeRounds(r.rounds) };
  },
  status: async (): Promise<{ services: ServiceHealth[] }> => {
    const r = await req<{ services?: unknown }>('/api/status');
    return { services: normalizeServices(r.services) };
  },
};

export function formatMinor(minor: number, currency: string): string {
  const value = minor / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}
